import type {SocialMediaProfileLinksMap, SocialMediaLinksValidationResult, ValidFormattedSocialMediaLinks} from '@Components/social-media-profile-links/social-media-profile-links.types';
import {
  PROFILE_LINK_HANDLE_HOSTS,
  PROFILE_LINK_HANDLE_PATTERN,
  PROFILE_LINK_HANDLE_PREFIX,
  PROFILE_LINK_HOST_PATTERNS,
  PROFILE_LINK_INVISIBLE_PADDING_PATTERN,
  PROFILE_LINK_MAX_LENGTH,
  PROFILE_LINK_PATH_SEPARATOR,
  PROFILE_LINK_SCHEME_LESS_DOMAIN_PATTERN,
  PROFILE_LINK_SCHEME_PATTERN,
  PROFILE_LINK_UNSAFE_CHARACTER_PATTERN,
  SocialMediaProfile,
} from '@Components/social-media-profile-links/social-media-profile-links.types';
import {repoURL} from '@Libraries/s3-library';
import {getReadBucket} from '@Utils/s3.util';

export const initSocialMediaProfileLinks = (): SocialMediaProfileLinksMap => {
  return {
    [SocialMediaProfile.INSTAGRAM]: '',
    [SocialMediaProfile.FACEBOOK]: '',
    [SocialMediaProfile.X]: '',
    [SocialMediaProfile.YOUTUBE]: '',
    [SocialMediaProfile.TIKTOK]: '',
    [SocialMediaProfile.PINTEREST]: '',
    [SocialMediaProfile.LINKEDIN]: '',
  };
};

export const initSocialMediaLinksErrorStates = (): Record<SocialMediaProfile, boolean> => {
  return {
    [SocialMediaProfile.INSTAGRAM]: false,
    [SocialMediaProfile.FACEBOOK]: false,
    [SocialMediaProfile.X]: false,
    [SocialMediaProfile.YOUTUBE]: false,
    [SocialMediaProfile.TIKTOK]: false,
    [SocialMediaProfile.PINTEREST]: false,
    [SocialMediaProfile.LINKEDIN]: false,
  };
};

export const initSocialMediaLinksVisibilityStates = (socialMediaProfileLinks: SocialMediaProfileLinksMap): Record<SocialMediaProfile, boolean> => {
  return {
    [SocialMediaProfile.INSTAGRAM]: true,
    [SocialMediaProfile.FACEBOOK]: true,
    [SocialMediaProfile.X]: socialMediaProfileLinks[SocialMediaProfile.X] !== '',
    [SocialMediaProfile.YOUTUBE]: socialMediaProfileLinks[SocialMediaProfile.YOUTUBE] !== '',
    [SocialMediaProfile.TIKTOK]: socialMediaProfileLinks[SocialMediaProfile.TIKTOK] !== '',
    [SocialMediaProfile.PINTEREST]: socialMediaProfileLinks[SocialMediaProfile.PINTEREST] !== '',
    [SocialMediaProfile.LINKEDIN]: socialMediaProfileLinks[SocialMediaProfile.LINKEDIN] !== '',
  };
};

export const initSocialMediaAccountsUsernames = (): Record<SocialMediaProfile, string[]> => {
  return {
    [SocialMediaProfile.INSTAGRAM]: [],
    [SocialMediaProfile.FACEBOOK]: [],
    [SocialMediaProfile.X]: [],
    [SocialMediaProfile.YOUTUBE]: [],
    [SocialMediaProfile.TIKTOK]: [],
    [SocialMediaProfile.PINTEREST]: [],
    [SocialMediaProfile.LINKEDIN]: [],
  };
};

export const formatSocialMediaLinks = (socialMediaLinks: Record<SocialMediaProfile, string>): Record<SocialMediaProfile, string> => {
  const formattedUrls: Record<SocialMediaProfile, string> = initSocialMediaProfileLinks();

  Object.entries(socialMediaLinks).forEach(([platform, socialMediaUrl]) => {
    formattedUrls[platform as SocialMediaProfile] = formatSocialMediaURL(socialMediaUrl);
  });

  return formattedUrls;
};

export const validateSocialMediaLinks = (socialMediaLinks: SocialMediaProfileLinksMap): SocialMediaLinksValidationResult => {
  const errorMap = initSocialMediaLinksErrorStates();
  const validatedUrls: Record<SocialMediaProfile, string> = initSocialMediaProfileLinks();

  let allValid = true;

  Object.entries(socialMediaLinks).forEach(([platform, inputValue]) => {
    const socialPlatform = platform as SocialMediaProfile;
    const validatedURL = getCanonicalSocialProfileUrl(socialPlatform, inputValue);

    if (validatedURL !== null) {
      validatedUrls[socialPlatform] = validatedURL;
    } else {
      errorMap[socialPlatform] = true;
      allValid = false;
    }
  });

  return {
    isValid: allValid,
    validatedUrls,
    errorMap,
  };
};

export const getValidFormattedURLForSocialPlatform = (socialPlatform: SocialMediaProfile, url: string): string => {
  const validatedURL = getCanonicalSocialProfileUrl(socialPlatform, url);
  return formatSocialMediaURL(validatedURL ?? '');
};

export const formatSocialMediaURL = (socialMediaUrl: string): string => {
  if (socialMediaUrl.startsWith('http')) {
    return socialMediaUrl;
  }

  return socialMediaUrl !== '' ? `https://${socialMediaUrl}` : '';
};

export const getSocialMediaTypeMap = (): Record<string, SocialMediaProfile> => {
  return {
    instagram: SocialMediaProfile.INSTAGRAM,
    'ig-business': SocialMediaProfile.INSTAGRAM,
    facebook: SocialMediaProfile.FACEBOOK,
    fb_profile: SocialMediaProfile.FACEBOOK,
    fb_page: SocialMediaProfile.FACEBOOK,
    fb_group: SocialMediaProfile.FACEBOOK,
    x: SocialMediaProfile.X,
    twitter: SocialMediaProfile.X,
    youtube: SocialMediaProfile.YOUTUBE,
    'youtube-channel': SocialMediaProfile.YOUTUBE,
    tiktok: SocialMediaProfile.TIKTOK,
    'tiktok-profile': SocialMediaProfile.TIKTOK,
    pinterest: SocialMediaProfile.PINTEREST,
    'linkedin-page': SocialMediaProfile.LINKEDIN,
    'linkedin-profile': SocialMediaProfile.LINKEDIN,
  };
};

export const validateAndFormatSocialMediaLinks = (socialMediaLinks: SocialMediaProfileLinksMap): ValidFormattedSocialMediaLinks => {
  const {errorMap, isValid, validatedUrls} = validateSocialMediaLinks(socialMediaLinks);
  const formattedUrls = formatSocialMediaLinks(validatedUrls);

  return {
    isValid,
    formattedUrls,
    errorMap,
  };
}

export const getSocialIconPath = (iconName: string): string => {
  return repoURL(`assets/social-icons/original/${iconName}.png`, getReadBucket());
};

export const getCanonicalSocialProfileUrl = (platform: SocialMediaProfile, input: string | null): string | null => {
  const link = (input ?? '').replace(PROFILE_LINK_INVISIBLE_PADDING_PATTERN, '');

  if (link === '') {
    return '';
  }

  if (link.length > PROFILE_LINK_MAX_LENGTH || PROFILE_LINK_UNSAFE_CHARACTER_PATTERN.test(link)) {
    return null;
  }

  const handle = getProfileHandle(link);

  if (handle !== null) {
    return buildProfileUrlFromHandle(platform, handle);
  }

  const url = PROFILE_LINK_SCHEME_PATTERN.test(link) ? link : `https://${link}`;
  const isOnPlatformHost = PROFILE_LINK_HOST_PATTERNS[platform].some((hostPattern) => {
    return getProfileUsernameOnHost(url, hostPattern) !== null;
  });

  return isOnPlatformHost ? canonicaliseProfileUrlOrigin(url) : null;
};

export const getSafeSocialProfileHref = (platform: SocialMediaProfile, storedLink: string | null | undefined): string | null => {
  const canonicalUrl = getCanonicalSocialProfileUrl(platform, storedLink ?? '');
  return canonicalUrl === '' ? null : canonicalUrl;
};

export const canonicaliseSocialMediaLinks = (socialMediaLinks: Readonly<Record<string, string | undefined>>): SocialMediaProfileLinksMap => {
  const canonicalLinks = initSocialMediaProfileLinks();

  Object.values(SocialMediaProfile).forEach((platform) => {
    canonicalLinks[platform] = getCanonicalSocialProfileUrl(platform, socialMediaLinks[platform] ?? '') ?? '';
  });

  return canonicalLinks;
};

const getProfileUsernameOnHost = (url: string, hostPattern: string): string | null => {
  const pattern = new RegExp(`^https?://([a-z0-9-]+\\.)*(?:${hostPattern})/(?:#!/)?@?([^/?]*)`, 'i');
  const username = pattern.exec(url)?.[2] ?? '';
  return username === '' ? null : username;
};

const getProfileHandle = (link: string): string | null => {
  if (isUrlShaped(link)) {
    return null;
  }

  const candidate = link.startsWith(PROFILE_LINK_HANDLE_PREFIX) ? link.slice(PROFILE_LINK_HANDLE_PREFIX.length) : link;
  return PROFILE_LINK_HANDLE_PATTERN.test(candidate) ? candidate : null;
};

const isUrlShaped = (link: string): boolean => {
  return PROFILE_LINK_SCHEME_PATTERN.test(link) || link.includes(PROFILE_LINK_PATH_SEPARATOR) || PROFILE_LINK_SCHEME_LESS_DOMAIN_PATTERN.test(link);
};

const buildProfileUrlFromHandle = (platform: SocialMediaProfile, handle: string): string => {
  const path = platform === SocialMediaProfile.TIKTOK ? `${PROFILE_LINK_HANDLE_PREFIX}${handle}` : handle;
  return `https://${PROFILE_LINK_HANDLE_HOSTS[platform]}/${path}`;
};

const canonicaliseProfileUrlOrigin = (url: string): string => {
  return url.replace(/^https?:\/\/([^/]+)/i, (_match: string, host: string): string => {
    return `https://${host.toLowerCase()}`;
  });
};
